import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Stack, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import { OtpInput } from './OtpInput';
import { apiBaseUrl } from '../../auth/config';
import { useAccessToken } from '../../auth/useAccessToken';
import { useResetOwn2fa, useRegenerateBackupCodes } from '../../api/queries';

/**
 * Two related self-service flows wrapped in one dialog so the user
 * menu only needs one entry per surface:
 *
 *   - mode="reset"       → "Reset 2FA". Steps:
 *       1. Enter current TOTP (or backup code) — proof of liveness.
 *       2. Server clears the existing TOTP secret + unused backup
 *          codes; we POST /me/2fa/setup to mint a fresh secret and
 *          show the QR code.
 *       3. User scans on their new phone, enters the 6-digit code,
 *          we POST /me/2fa/confirm — server returns the fresh batch
 *          of backup codes.
 *       4. Backup codes shown for the user to copy / download.
 *
 *   - mode="regenerate"  → "Regenerate backup codes". One step:
 *       1. Enter current TOTP — proof of liveness.
 *       2. Server invalidates old codes, mints new ones, returns
 *          plaintext for one-time display.
 */
export type Manage2faDialogProps = {
  open: boolean;
  mode: 'reset' | 'regenerate';
  onClose: () => void;
};

type Step = 'verify' | 'scan' | 'confirm' | 'codes';

export function Manage2faDialog({ open, mode, onClose }: Manage2faDialogProps) {
  const reset = useResetOwn2fa();
  const regen = useRegenerateBackupCodes();
  const getToken = useAccessToken();

  const [step, setStep] = useState<Step>('verify');
  const [currentCode, setCurrentCode] = useState('');
  const [newCode, setNewCode]         = useState('');
  const [qr, setQr]                   = useState<{ qrCodeDataUri: string; manualEntryKey: string } | null>(null);
  const [codes, setCodes]             = useState<string[] | null>(null);
  const [error, setError]             = useState<string | null>(null);
  const [busy, setBusy]               = useState(false);

  useEffect(() => {
    // Only `open` belongs in the deps. The mutation objects (`reset`,
    // `regen`) get a new reference on every render — including them
    // here used to fire this effect on every render, calling
    // `reset.reset()` which ticks mutation state → re-render → effect
    // fires again → infinite loop. That pegged the main thread and
    // made every click feel like a 30-second wait.
    if (!open) {
      setStep('verify'); setCurrentCode(''); setNewCode('');
      setQr(null); setCodes(null); setError(null); setBusy(false);
      reset.reset(); regen.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function handleVerifyStep() {
    setError(null); setBusy(true);
    try {
      if (mode === 'regenerate') {
        const result = await regen.mutateAsync(currentCode);
        setCodes(result.backup_codes);
        setStep('codes');
      } else {
        // reset: clear secret first, then immediately mint a new one.
        await reset.mutateAsync(currentCode);
        const token = await getToken();
        const setupRes = await fetch(`${apiBaseUrl}/api/v1/me/2fa/setup`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!setupRes.ok) throw new Error(`Setup failed: HTTP ${setupRes.status}`);
        const setup = await setupRes.json() as { qrCodeDataUri: string; manualEntryKey: string };
        setQr(setup);
        setStep('scan');
      }
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Action failed.');
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmStep(submittedCode?: string) {
    setError(null); setBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${apiBaseUrl}/api/v1/me/2fa/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ Code: submittedCode ?? newCode }),
      });
      if (!res.ok) throw new Error(`Confirm failed: HTTP ${res.status}`);
      const data = await res.json() as { backup_codes: string[] };
      setCodes(data.backup_codes);
      setStep('codes');
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Confirm failed.');
    } finally {
      setBusy(false);
    }
  }

  const title = mode === 'reset' ? 'Reset two-factor authentication' : 'Regenerate backup codes';

  return (
    <Dialog open={open} onClose={step === 'codes' ? onClose : (busy ? undefined : onClose)}
            maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        {title}
        {!busy && <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>}
      </DialogTitle>
      <DialogContent dividers>
        {step === 'verify' && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {mode === 'reset'
                ? 'Enter a current 6-digit code from your existing authenticator (or a backup code) to prove you still control the account. Your existing TOTP and any unused backup codes will be cleared once you continue.'
                : 'Enter a current 6-digit code from your authenticator to confirm it\'s really you. All previous backup codes will be invalidated and replaced.'}
            </Typography>
            <OtpInput value={currentCode} onChange={setCurrentCode}
              onComplete={() => { /* user clicks Continue */ }} />
            {error && <ErrorAlert>{error}</ErrorAlert>}
          </Stack>
        )}

        {step === 'scan' && qr && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Scan the QR with any authenticator — iCloud Passwords
              (iPhone Settings → Passwords), Microsoft / Google
              Authenticator, Authy, 1Password — then enter the 6-digit
              code it shows to finish.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box component="img" src={qr.qrCodeDataUri} alt="TOTP QR"
                   sx={{ width: 220, height: 220, p: 1, bgcolor: '#fff',
                         border: 1, borderColor: 'divider', borderRadius: 1.5 }} />
            </Box>
            <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                iPhone tip
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                Open Camera, point at the QR, tap "Add Verification Code" — saved into
                Settings → Passwords for auto-fill next time you sign in here or on the
                ARMS Technician app.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Manual entry key (paste into any authenticator)
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{
                  fontFamily: 'monospace', bgcolor: 'action.hover',
                  px: 1.5, py: 0.5, borderRadius: 1, letterSpacing: 1,
                }}>
                  {qr.manualEntryKey}
                </Typography>
                <Button size="small" variant="outlined" onClick={() => {
                  navigator.clipboard.writeText(qr.manualEntryKey);
                }}>Copy</Button>
              </Stack>
            </Box>
            <OtpInput value={newCode} onChange={setNewCode}
              onComplete={(c) => { void handleConfirmStep(c); }} />
            {error && <ErrorAlert>{error}</ErrorAlert>}
          </Stack>
        )}

        {step === 'codes' && codes && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="success">
              {mode === 'reset' ? 'Two-factor authentication has been reset.' : 'Backup codes regenerated.'}
              {' '}Save these somewhere safe — they will not be shown again.
            </Alert>
            <Box sx={{
              p: 2, borderRadius: 1, bgcolor: 'background.default',
              border: 1, borderColor: 'divider',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1,
            }}>
              {codes.map((c) => (
                <Typography key={c} variant="body2" sx={{
                  fontFamily: 'monospace', fontSize: 14, textAlign: 'center', py: 0.5,
                }}>{c}</Typography>
              ))}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" fullWidth onClick={() => {
                navigator.clipboard.writeText(codes.join('\n'));
              }}>Copy all</Button>
              <Button variant="outlined" fullWidth onClick={() => {
                const blob = new Blob([codes.join('\n')], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'arms-backup-codes.txt'; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 5_000);
              }}>Download .txt</Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'verify' && (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" disabled={currentCode.length < 6 || busy}
              onClick={handleVerifyStep}>
              {busy ? 'Verifying…' : 'Continue'}
            </Button>
          </>
        )}
        {step === 'scan' && (
          <>
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="contained" disabled={newCode.length < 6 || busy}
              onClick={() => handleConfirmStep()}>
              {busy ? 'Confirming…' : 'Confirm and finish'}
            </Button>
          </>
        )}
        {step === 'codes' && (
          <Button variant="contained" onClick={onClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
